import React, { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { H2, Line, Button, Flex } from '../../lib/components'
import { DeleteIcon } from '../../lib/components/Icon'
import { NewsletterGroupFormData } from '../../services/newsletter-service'
import './newsletter-group-form.scss'
import { CircularProgress } from '@mui/material'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX_EMAILS = 100

type NewsletterGroupFormPresenterProps = {
  form: UseFormReturn<NewsletterGroupFormData>
  isSubmitting: boolean
  isLoading: boolean
  onSubmit: (data: NewsletterGroupFormData) => Promise<void>
  isEditMode: boolean
}

function NewsletterGroupFormPresenter({
  form,
  isSubmitting,
  isLoading,
  onSubmit,
  isEditMode,
}: NewsletterGroupFormPresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = form

  const emails = watch('emails')
  const [emailInput, setEmailInput] = useState('')
  const [emailInputError, setEmailInputError] = useState('')

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase()
    if (!email) return

    if (!EMAIL_REGEX.test(email)) {
      setEmailInputError('Please enter a valid email address')
      return
    }
    if (emails.includes(email)) {
      setEmailInputError('This email has already been added')
      return
    }
    if (emails.length >= MAX_EMAILS) {
      setEmailInputError(`Maximum ${MAX_EMAILS} emails allowed`)
      return
    }

    setValue('emails', [...emails, email], { shouldValidate: true, shouldDirty: true })
    clearErrors('emails')
    setEmailInput('')
    setEmailInputError('')
  }

  const removeEmail = (emailToRemove: string) => {
    setValue(
      'emails',
      emails.filter((e) => e !== emailToRemove),
      { shouldValidate: true, shouldDirty: true },
    )
  }

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(emailInput)
    }
  }

  const handleEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const pastedEmails = pastedText.split(/[,;\s\n]+/).filter(Boolean)

    let hasError = false
    const newEmails = [...emails]

    for (const raw of pastedEmails) {
      const email = raw.trim().toLowerCase()
      if (!email) continue
      if (newEmails.length >= MAX_EMAILS) {
        setEmailInputError(`Maximum ${MAX_EMAILS} emails allowed`)
        hasError = true
        break
      }
      if (!EMAIL_REGEX.test(email)) continue
      if (newEmails.includes(email)) continue
      newEmails.push(email)
    }

    if (newEmails.length !== emails.length) {
      setValue('emails', newEmails, { shouldValidate: true, shouldDirty: true })
      clearErrors('emails')
    }
    if (!hasError) {
      setEmailInput('')
      setEmailInputError('')
    }
  }

  return (
    <>
      {isLoading && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}

      <Flex variant='column-center'>
        <H2
          classes={['mb-24']}
          text={isEditMode ? 'Edit Newsletter Group' : 'Create Newsletter Group'}
        />

        <form className='group-form' onSubmit={handleSubmit(onSubmit)} noValidate>
          <Line />

          <div className='group-form__field'>
            <label className='group-form__label' htmlFor='name'>
              Name <span className='group-form__required'>*</span>
            </label>
            <input
              id='name'
              className={`group-form__input ${errors.name ? 'group-form__input--error' : ''}`}
              placeholder='Enter group name'
              maxLength={500}
              {...register('name', {
                required: 'Name is required',
                maxLength: { value: 500, message: 'Name must be at most 500 characters' },
              })}
            />
            {errors.name && <span className='group-form__error'>{errors.name.message}</span>}
          </div>

          <div className='group-form__field'>
            <label className='group-form__label' htmlFor='description'>
              Description <span className='group-form__required'>*</span>
            </label>
            <textarea
              id='description'
              className={`group-form__input group-form__textarea ${
                errors.description ? 'group-form__input--error' : ''
              }`}
              placeholder='Enter group description'
              maxLength={500}
              rows={3}
              {...register('description', {
                required: 'Description is required',
                maxLength: { value: 500, message: 'Description must be at most 500 characters' },
              })}
            />
            {errors.description && (
              <span className='group-form__error'>{errors.description.message}</span>
            )}
          </div>

          <div className='group-form__field'>
            <label className='group-form__label' htmlFor='emailInput'>
              Emails <span className='group-form__required'>*</span>
              <span className='group-form__hint-inline'>
                ({emails.length}/{MAX_EMAILS})
              </span>
            </label>
            <div
              className={`group-form__email-input-wrapper ${
                errors.emails || emailInputError ? 'group-form__email-input-wrapper--error' : ''
              }`}
            >
              {emails.length > 0 && (
                <div className='group-form__email-tags'>
                  {emails.map((email) => (
                    <span key={email} className='group-form__email-tag'>
                      {email}
                      <span
                        className='group-form__email-tag-remove'
                        onClick={() => removeEmail(email)}
                      >
                        <DeleteIcon dataTestId={`remove-email-${email}`} />
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <input
                id='emailInput'
                className='group-form__email-input'
                placeholder={
                  emails.length >= MAX_EMAILS
                    ? 'Maximum emails reached'
                    : 'Type an email and press Enter'
                }
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value)
                  setEmailInputError('')
                }}
                onKeyDown={handleEmailKeyDown}
                onPaste={handleEmailPaste}
                onBlur={() => {
                  if (emailInput.trim()) addEmail(emailInput)
                }}
                disabled={emails.length >= MAX_EMAILS}
              />
            </div>
            <span className='group-form__hint'>
              Press Enter or comma to add. You can also paste multiple emails separated by commas.
            </span>
            {emailInputError && <span className='group-form__error'>{emailInputError}</span>}
            {errors.emails && <span className='group-form__error'>{errors.emails.message}</span>}
          </div>

          <Line />

          <div className='group-form__actions'>
            <Button
              buttonType='submit'
              variant='primary-btn'
              handleClick={() => {}}
              text={isSubmitting ? 'Saving...' : isEditMode ? 'Update Group' : 'Create Group'}
              isDisabled={isSubmitting}
            />
          </div>
        </form>
      </Flex>
    </>
  )
}

export { NewsletterGroupFormPresenter }
